// uploadAudioFile — worker records a new audio segment via MediaRecorder
// (or picks a file from disk); we PUT the raw blob to audio-uploads/{code}-raw.{ext},
// then create a placeholder /audios/{code} doc. The transcodeAudioUpload Cloud
// Function picks it up, transcodes to mp3, and fills storagePath / duration.
//
// Until the CF finishes, the doc has placeholder: true so AudioRelease.tsx
// shows「· 轉檔中…」instead of「· 釋出」.

import { audioDoc, audiosCol, getFirebase } from "@goodbye/firebase";
import { getDocs } from "firebase/firestore";
import { setDoc } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";

export interface UploadAudioFileInput {
  blob: Blob;
  title: string;
  email: string;
  /** e.g. "audio/webm" / "audio/mp4" / "audio/mpeg" — drives the extension. */
  mimeType: string;
}

export interface UploadAudioFileResult {
  audioId: string;
  code: string;
  sourceUploadPath: string;
}

/**
 * Pick a sensible file extension from a MediaRecorder / File mimeType.
 * Falls back to ".bin" — the CF reads the actual contents anyway.
 */
function extFromMime(mimeType: string): string {
  // Strip codecs: "audio/webm;codecs=opus" → "audio/webm"
  const base = mimeType.split(";")[0].trim().toLowerCase();
  switch (base) {
    case "audio/webm":
      return "webm";
    case "audio/ogg":
      return "ogg";
    case "audio/mp4":
    case "audio/x-m4a":
      return "m4a";
    case "audio/aac":
      return "aac";
    case "audio/mpeg":
    case "audio/mp3":
      return "mp3";
    case "audio/wav":
    case "audio/x-wav":
      return "wav";
    case "audio/flac":
      return "flac";
    default:
      return "bin";
  }
}

/**
 * Compute the next available audio code by scanning /audios for the max
 * numeric order. Original 15 docs use 1-15; worker-added recordings get
 * 16, 17, … with zero-padded codes ("16", "17"...).
 */
async function nextAudioCode(): Promise<{ code: string; order: number }> {
  const snap = await getDocs(audiosCol());
  let maxOrder = 0;
  snap.forEach((d) => {
    const o = d.data().order;
    if (typeof o === "number" && o > maxOrder) maxOrder = o;
  });
  const order = maxOrder + 1;
  const code = String(order).padStart(2, "0");
  return { code, order };
}

export async function uploadAudioFile(
  input: UploadAudioFileInput,
): Promise<UploadAudioFileResult> {
  const { blob, title, email, mimeType } = input;
  const trimmedTitle = title.trim();
  if (!trimmedTitle) throw new Error("title is required");
  if (blob.size === 0) throw new Error("audio blob is empty");

  const { code, order } = await nextAudioCode();
  const ext = extFromMime(mimeType);
  const sourceUploadPath = `audio-uploads/${code}-raw.${ext}`;

  const storage = getFirebase().storage;
  await uploadBytes(ref(storage, sourceUploadPath), blob, {
    contentType: mimeType || "application/octet-stream",
  });

  await setDoc(audioDoc(code), {
    code,
    title: trimmedTitle,
    intro: "",
    duration: 0,
    place: "",
    storagePath: "",
    released: false,
    order,
    placeholder: true,
    sourceUploadPath,
    uploadedBy: email,
  });

  return { audioId: code, code, sourceUploadPath };
}
